import { css } from 'styled-components';
import styled from 'styled-components/macro';

import { ErrorIcon } from '../Input/InputElements';

import { IDropdown } from './Dropdown';

export const Wrapper = styled.div<IDropdown.TWrapper>`
  position: relative;
  display: flex;
  padding-top: 16.5px;
  margin: ${({ marginWrapper }) => marginWrapper && marginWrapper};
`;

export const ButtonDrop = styled.button`
  background-color: ${({ theme }) => theme.colors.gray800};
  border: ${({ theme }) => theme.colors.gray200} 1px solid;
  border-radius: ${({ theme }) => theme.spacing.xs};
  padding: 0 12px;
  font-style: normal;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.gray0};
  font-size: ${({ theme }) => theme.typeSize.sm};
  line-height: ${({ theme }) => theme.typeSize.xs};
  height: 40px;
  flex: 1;
  text-align: start;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    box-shadow: 0 4px 4px rgba(6, 199, 141, 0.1);
    border-color: ${({ theme }) => theme.colors.gray400};
  }

  &:focus {
    border: ${({ theme }) => theme.colors.gray500} 1px solid;

    :hover {
      box-shadow: 0 4px 4px rgba(6, 199, 141, 0.15);
    }
  }
`;

export const Label = styled.label<IDropdown.TTitle>`
  font-style: normal;
  font-weight: 400;
  text-transform: capitalize;
  font-size: ${({ theme }) => theme.typeSize.xs};
  line-height: ${({ theme }) => theme.typeSize.xs};
  margin: 0 0 2.5px 0;
  transition: 0.2s ease all;
  color: ${({ theme, isOpen, hasValue }) =>
    isOpen || hasValue ? theme.colors.gray0 : theme.colors.gray600};
  position: absolute;
  top: ${({ isOpen, hasValue }) => (isOpen || hasValue ? '0' : '30px')};
  left: ${({ isOpen, hasValue }) => (isOpen || hasValue ? '0' : '12px')};

  :hover {
    color: ${({ theme }) => theme.colors.green700};
    font-size: ${({ theme }) => theme.typeSize.sm};
    top: 0;
    left: 0;
  }

  ${ButtonDrop}:hover ~ & {
    color: ${({ theme }) => theme.colors.green700};
    font-size: ${({ theme }) => theme.typeSize.sm};
    top: 0;
    left: 0;
  }
`;

export const TickIcon = styled.span`
  position: absolute;
  bottom: 14px;
  right: 36px;
  font-size: 11px;

  :before {
    color: ${({ theme }) => theme.colors.green500};
  }
`;

export const Container = styled.div<{ isShow: boolean }>`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  top: ${({ isShow }) => (isShow ? 0 : '100%')};
  background: rgba(0, 0, 0, 0.56);
  box-shadow: 0 2px 7px rgba(0, 0, 0, 0.08);
  z-index: 999;
  opacity: ${({ isShow }) => (isShow ? 1 : 0)};
  transition: opacity 0.15s ease-in-out;
  align-items: start;
  backdrop-filter: blur(2px);
  display: flex;
`;

const transitionAxisY = () => {
  return css`
    @keyframes enter {
      0% {
        ${{
          transform: `translateY(100%)`,
        }}
      }
      100% {
        transform: translateY(0);
      }
    }

    @keyframes out {
      0% {
        transform: translateY(0);
      }
      100% {
        ${{
          transform: `translateY(100%)`,
        }}
      }
    }
  `;
};

export const Content = styled.div<{ isShow: boolean }>`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  max-height: ${({ isShow }) => (isShow ? '70vh' : '0')};
  background-color: ${({ theme }) => theme.colors.gray800};
  box-shadow: 0 2px 7px rgba(0, 0, 0, 0.08);
  border-radius: 12px 12px 0 0;
  padding: ${({ isShow }) => (isShow ? '24px' : 0)};
  z-index: 7;
  opacity: ${({ isShow }) => (isShow ? 1 : 0)};
  animation: ${({ isShow }) => (isShow ? 'enter' : 'out')};
  transition: opacity 0.3s ease-in-out, max-height 0.3s ease-in-out, min-height 0.3s ease-in-out;
  flex: 1;
  ${() => transitionAxisY()};

  .search-box {
    margin: -20px 0 12px 0;
  }
`;

export const Header = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 16px;
`;

export const Title = styled.div`
  font-style: normal;
  font-weight: 600;
  font-size: 18px;
  line-height: 24px;
  margin-top: 3px;
  color: ${({ theme }) => theme.colors.gray300};
  text-transform: capitalize;
`;

export const CloseButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  transform: rotate(180deg);
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background-color: ${({ theme }) => theme.colors.gray900};
  border: none;

  :before {
    color: ${({ theme }) => theme.colors.gray300};
  }
`;

export const Placeholder = styled.label`
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 12px;
  color: ${({ theme }) => theme.colors.gray800};
  user-select: none;
`;

export const ChevronIconAnimate = styled.span<IDropdown.TIcon>`
  position: absolute;
  bottom: 14px;
  right: 12px;
  font-size: 16px;

  :before {
    color: ${({ theme }) => theme.colors.gray500};
  }

  animation: rotate linear infinite;
  transition-duration: 0.4s;
  transform: ${({ isOpen }) => (!isOpen ? 'rotate(0deg)' : 'rotate(180deg)')};
  z-index: ${({ isOpen }) => isOpen && 3};
`;

export const DropMenu = styled.ul`
  overflow: hidden;
  overflow-y: scroll;
  margin: 0;
  padding: 0;

  ::-webkit-scrollbar {
    width: 8px;
  }

  ::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) => theme.colors.green200};
    border-radius: 8px;
  }
`;

export const Tooltip = styled.div`
  background: ${({ theme }) => theme.colors.red500};
  border-radius: 6px;
  -moz-border-radius: 6px;
  -webkit-border-radius: 6px;
  position: absolute;
  bottom: -31px;
  right: 6px;
  padding: 8px 4px 6px 4px;
  color: ${({ theme }) => theme.colors.gray0};
  font-style: normal;
  font-weight: 400;
  font-size: 10px;
  line-height: 10px;
  opacity: 1;
  pointer-events: none;

  ${ErrorIcon}:hover ~ & {
    opacity: 1;
    transform: translate3d(0, 10px, 0);
    transition: all 0.15s ease-in-out;
  }

  :after {
    content: '';
    border: 7px solid transparent;
    position: absolute;
    top: -12px;
    border-bottom-color: ${({ theme }) => theme.colors.red500};
    right: 7px;
  }
`;

export const Item = styled.li`
  display: flex;
  align-items: center;
  gap: 8px;
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 12px;
  color: ${({ theme }) => theme.colors.gray0};
  user-select: none;
  border-radius: 8px;
  transition: 0.2s ease all;
  padding: 10px 12px;
  margin: 10px 12px;

  :hover {
    background-color: ${({ theme }) => theme.colors.gray700};
    color: ${({ theme }) => theme.colors.gray0};
  }
`;
